import { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';

import { Box, Flex, Stack, useToast } from "@chakra-ui/react";
import WarningTextMessage from '../../../global/components/helper-components/components/WarningTextMessage';

import { createNewOrgUser, createOrgUserProfileReset } from '../../../profile/actions/admin-user-actions'

import { BsCheckCircle, BsCircle } from 'react-icons/bs';
import { redirectToNewPage, triggerLaunchdeckToast, validateEmail } from '../../../global/helpers/global-helpers';
import LaunchdeckButton from '../../../global/components/helper-components/components/LaunchdeckButton';
import { displayReactIcon } from '../../../digital-sales-room/helpers/dsr-display-helpers';
import LaunchdeckModalButtonBar from '../../../global/components/helper-components/components/LaunchdeckModalButtonBar';
import LaunchdeckFormInput from '../../../forms/TextInputField/components/LaunchdeckFormInput';
import LaunchdeckShortLogo from '../../../global/components/helper-components/components/LaunchdeckShortLogo';


function AddNewOrgUserForm({
	role,
	orgIdToken,
	handleCloseModal,
	// Redux
	manageAdminOrgUsersOutcome,
	isMobileView,
	// Actions
	createNewOrgUser,
	createOrgUserProfileReset
}) {

	const toast = useToast();
	const history = useHistory()

	const [showNoRoleError, setShowNoRoleError] = useState(false);
	const [addingInProgress, setAddingInProgress] = useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)
	const [showUserFromOtherOrgModal, setShowUserFromOtherOrgModal] = useState(false)
	const [attemptedUserEmail, setAttemptedUserEmail] = useState("")
	const [showUnableToAddInactiveUser, setShowUnableToAddInactiveUser] = useState(false)

	// Inputs
	const [chosenRole, setChosenRole] = useState(role)
	const [memberEmail, setMemberEmail] = useState("")
	const [userOrgTitleDisplay, setUserOrgTitle] = useState("")
	const [memberFirstNameDisplay, setUserFirstName] = useState("")
	const [memberLastNameDisplay, setUserLastName] = useState("")

	// Input validation Errors
	const [showMissingEmailError, setShowMissingEmailError] = useState(false)
	const [showMissingTitleError, setShowMissingTitleError] = useState(false)
	const [showMissingFirstNameError, setShowMissingFirstNameError] = useState(false)
	const [showMissingLastNameError, setShowMissingLastNameError] = useState(false)
	const [emailFailedValidation, setEmailFailedValidation] = useState(false)


	// UX & State Management
	useEffect(() => {
		if (manageAdminOrgUsersOutcome) {
			const { updatedSuccessfully, errorMessage, isUnauthorised, isInactiveUser, isFailedEmailValidation,
				duplicateUserInOrg, addedMemberFromOtherOrg, attemptedUserEmail } = manageAdminOrgUsersOutcome || {};

			const closeModalAndReset = () => {
				setAddingInProgress(false)
				setAttemptedUserEmail("")
				handleCloseModal()
				createOrgUserProfileReset()
			}

			if (isUnauthorised === true) {
				redirectToNewPage('/invalid')
			}

			if (updatedSuccessfully === true) {
				toast(
					triggerLaunchdeckToast({
						useCase: "show-success-state",
						// bannerText: "",
						label: "New user successfully created & added to your team",
						isMobileView
					})
				)

				closeModalAndReset()
			}

			if (updatedSuccessfully === false) {
				if (duplicateUserInOrg) {
					setShowConfirmationModal(true)
					createOrgUserProfileReset()
				}

				if (addedMemberFromOtherOrg) {
					setAttemptedUserEmail(attemptedUserEmail)
					setShowUserFromOtherOrgModal(true)
					createOrgUserProfileReset()
				}

				if (isInactiveUser) {
					setAttemptedUserEmail(attemptedUserEmail)
					setShowUnableToAddInactiveUser(true)
					createOrgUserProfileReset()
				}

				if (isFailedEmailValidation) {
					setEmailFailedValidation(true)
					createOrgUserProfileReset()
				}

				if (errorMessage) {
					toast(
						triggerLaunchdeckToast({
							useCase: "show-error-state",
							bannerText: "Error",
							label: errorMessage,
							isMobileView
						})
					)

					closeModalAndReset()
				}


				setAddingInProgress(false)
			}
		}
	}, [manageAdminOrgUsersOutcome, createOrgUserProfileReset, handleCloseModal, toast]);

	// Component functionality
	const handleChange = (event) => {
		const value = event.target.value;
		const variableName = event.target.name;
		switch (variableName) {
			case ('userOrgTitle'): {
				setUserOrgTitle(value)
				break;
			}
			case ('memberFirstName'): {
				setUserFirstName(value)
				break;
			}
			case ('memberLastName'): {
				setUserLastName(value)
				break;
			}
			case ('memberEmail'): {
				setMemberEmail(value)
				break;
			}
			default: {
				console.log('There is no variable with this name', variableName)
				break;
			}
		}
	}

	const handleChooseRole = (role) => {
		// Scenario: When user wants to remove his choice 
		if (chosenRole === role) {
			setChosenRole('')
			setShowNoRoleError(false)
		} else {
			// Scenario: When user picks a role for the first time
			// Scenario: Also when user switches from one role to the other role
			setChosenRole(role)
			setShowNoRoleError(false)
		}
	}

	const handleCreateNewUser = () => {
		const emailValidated = validateEmail(memberEmail)

		if (!userOrgTitleDisplay) {
			setShowMissingTitleError(true)
		} else if (userOrgTitleDisplay) {
			setShowMissingTitleError(false)
		}

		if (!memberFirstNameDisplay) {
			setShowMissingFirstNameError(true)
		} else if (memberFirstNameDisplay) {
			setShowMissingFirstNameError(false)
		}

		if (!memberLastNameDisplay) {
			setShowMissingLastNameError(true)
		} else if (memberLastNameDisplay) {
			setShowMissingLastNameError(false)
		}

		if (!emailValidated) {
			setShowMissingEmailError(true)
		} else if (emailValidated) {
			setShowMissingEmailError(false)
		}

		if (!chosenRole) {
			setShowNoRoleError(true)
		} else if (chosenRole) {
			setShowNoRoleError(false)
		}

		if (chosenRole && userOrgTitleDisplay && memberFirstNameDisplay && memberLastNameDisplay && emailValidated === true) {
			setAddingInProgress(true)

			let newOrgUserProperties = {
				updateEvent: 'add-new-org-user',
				email: memberEmail.trim(),
				firstName: memberFirstNameDisplay.trim(),
				lastName: memberLastNameDisplay.trim(),
				userOrgTitle: userOrgTitleDisplay.trim(),
				chosenRole: chosenRole
			}

			createNewOrgUser(newOrgUserProperties, orgIdToken)

		}
	}

	const handleGetSupport = (source) => {

		let suggestedSupportMessage = "";
		switch (source) {
			case ("deactivated-user"): {
				suggestedSupportMessage = `Hi there, I would like to add a member to my team, but it says they have been deactivated or offboarded. Can you please help?\n\nSome helpful information:\n\nEmail:${attemptedUserEmail}`

				break;
			}
			case ("user-from-another-org"): {
				suggestedSupportMessage = `Hi there, I would like to add a member to my team, but it says they belong to another organization currently. Can you please help?\n\nSome helpful information:\n\nEmail:${attemptedUserEmail}`
				break;
			}
			default: {
				break;
			}
		}

		history.push(`/support`, {
			suggestedSupportMessage
		})
	}


	if (showConfirmationModal === false && showUserFromOtherOrgModal === false && showUnableToAddInactiveUser === false) {
		return (
			<Flex align='center' w='100%' maxW={['inherit', '35em']} direction='column' overflowY='auto' maxHeight='80vh' >
				<Stack w='100%' maxW={['inherit', '35em']} px='2em'>

					<Box>
						<Flex p={['0.75em 0.5em', '1em']} justify='flex-start' align='center' textAlign={['center', 'left']} w='100%' color='brandDark.500' bg='brand.50' rounded='0.5em' fontSize='0.8em' my='1em'>
							A seat will be added to your Organization for this new user
						</Flex>
					</Box>

					<Box>
						<Box className='section-header-label' mt='1em' color='brandDark.500' fontSize={['0.8em', '0.875em']}>ROLE</Box>


						<Flex direction={['column', 'row', 'row', 'row']} gap='1em' justify='space-evenly' align='stretch' mt='1em'
							sx={{ pointerEvents: addingInProgress ? "none" : "auto", opacity: addingInProgress ? 0.5 : 1 }}
						>
							<Box flex='1' border='1px solid' borderColor={(chosenRole === 'admin-user') ? 'brand.300' : 'gray.300'} bg={(chosenRole === 'admin-user') && 'gray.50'} rounded='0.25em' py='0.75em' px='1.25em' cursor='pointer' onClick={() => handleChooseRole('admin-user')}>
								<Flex w='100%' gap='0.8em' justify='flex-start' align='center'>
									<Box className='flip-in-hor-bottom'>
										{
											(chosenRole === 'admin-user') ? <Box as={BsCheckCircle} className='flip-in-hor-bottom' color='brand.500' /> :
												<Box as={BsCircle} className='flip-in-hor-bottom' color='gray.300' />
										}
									</Box>
									<Flex color={(chosenRole === 'admin-user') ? 'brandDark.700' : 'gray.500'} lineHeight='1.375em' align='center' gap={4}>Administrator {displayReactIcon('administrator', '1.25em', '', '', 'brand.500')} </Flex>
								</Flex>
								<Box>

									<Box mt='0.5em' fontSize='0.75em' color='gray.500' fontWeight='400'> Manage users, billing, and set organization settings</Box>
								</Box>
							</Box>


							<Box flex='1' border='1px solid' borderColor={(chosenRole === 'basic-user') ? 'brandPink.300' : 'gray.300'} bg={(chosenRole === 'basic-user') && 'gray.50'} rounded='0.25em' py='0.75em' px='1.25em' cursor='pointer' onClick={() => handleChooseRole('basic-user')}>
								<Flex w='100%' gap='0.8em' justify='flex-start' align='center'>
									<Box className='flip-in-hor-bottom'>
										{
											(chosenRole === 'basic-user') ? <Box as={BsCheckCircle} className='flip-in-hor-bottom' color='brandPink.500' /> :
												<Box as={BsCircle} className='flip-in-hor-bottom' color='gray.300' />
										}
									</Box>
									<Flex w='100%' color={(chosenRole === 'basic-user') ? 'brandDark.700' : 'gray.500'} lineHeight='1.375em' align='center' gap={4}>User {displayReactIcon('user', '1.25em', '', '', 'brandPink.500')}</Flex>
								</Flex>

								<Box mt='0.5em' fontSize='0.75em' color='gray.500' fontWeight='400'> Access features & settings set by their Administrators</Box>

							</Box>
						</Flex>

						<Box mt='0.5em'>
							<WarningTextMessage errorLogic={showNoRoleError} message='Please indicate a role for this user' />
						</Box>


						<Box className='section-header-label' mt='3em' mb='1em' color='brandDark.500' fontSize={['0.8em', '0.875em']}>About this user</Box>


						<Flex direction='column' gap='0.25em'>
							<LaunchdeckFormInput
								label='Email'
								name={'memberEmail'}
								onChange={handleChange}
								value={memberEmail}
								isRequired={true}

								isDisabled={addingInProgress}
								errorLogic={showMissingEmailError || emailFailedValidation}
							/>

							<Flex w='100%' gap='1em' justify='space-between' align='flex-start'>
								<Box flex='1' >
									<LaunchdeckFormInput
										label='First name'
										name={'memberFirstName'}
										onChange={handleChange}
										value={memberFirstNameDisplay}
										isRequired={true}

										isDisabled={addingInProgress}
										errorLogic={showMissingFirstNameError}
									/>

								</Box>

								<Box flex='1'>
									<LaunchdeckFormInput
										label='Last name'
										name={'memberLastName'}
										onChange={handleChange}
										value={memberLastNameDisplay}
										isRequired={true}

										isDisabled={addingInProgress}
										errorLogic={showMissingLastNameError}
									/>

								</Box>
							</Flex>


							<LaunchdeckFormInput
								label='Title'
								name={'userOrgTitle'}
								onChange={handleChange}
								value={userOrgTitleDisplay}
								isRequired={true}

								isDisabled={addingInProgress}
								errorLogic={showMissingTitleError}
							/>
						</Flex>


						<WarningTextMessage
							errorLogic={emailFailedValidation || showMissingTitleError || showMissingLastNameError || showMissingFirstNameError || showMissingEmailError}
							message={emailFailedValidation ? 'Please use a valid email' : "Please provide required user information"}
						/>

					</Box>

				</Stack>



				<LaunchdeckModalButtonBar
					fontSize='1em'
					loadingLogic={addingInProgress}
					ctaFunction={() => handleCreateNewUser()}
					ctaLabel='Add User'
					noCancelOption={true}
				/>
			</Flex>
		)
	}

	if (showConfirmationModal === true) {
		return (
			<Flex direction='column' justify='center' align='center' gap='1em' p='2em' >

				<LaunchdeckShortLogo boxSize='2em' />
				<Box fontSize='0.875em'>
					{memberEmail} is already a member of your team
				</Box>

				<Flex mt='1em' w='100%' justify='center'>
					<LaunchdeckButton bg='brand.500' color='white' onClick={() => handleCloseModal()} label='OK' />
				</Flex>

			</Flex>
		)

	}

	if (showUserFromOtherOrgModal === true) {
		return (
			<Box p='2em' className='fade-in' fontSize='0.875em'>
				<Box>
					This member belongs to another organization and cannot be added to yours
				</Box>

				<Box my='1em'>
					If you think there has been an error, get in touch with Support, and we'll look into it immediately.
				</Box>

				<Flex mt='1.5em' w='100%' justify='flex-end'>
					<LaunchdeckButton bg='brandPink.500' color='white' mx="0.5em" onClick={() => handleGetSupport("user-from-another-org")} label='Get support' />
					{/* <LaunchdeckButton bg='brand.500' color='white' mx="0.5em" onClick={() => handleCloseModal()} label='OK' /> */}
				</Flex>

			</Box>
		)
	}

	if (showUnableToAddInactiveUser === true) {
		return (
			<Box p='2em' className='fade-in' fontSize='0.875em'>
				<Box>
					You are attempting to add a deactivated or offboarded member.
				</Box>

				<Box my='1em'>
					If you think there has been an error, get in touch with Support, and we'll look into it immediately.
				</Box>

				<Flex mt='1.5em' w='100%' justify='center'>
					<LaunchdeckButton bg='brandPink.500' color='white' mx="0.5em" onClick={() => handleGetSupport("deactivated-user")} label='Get support' />
					<LaunchdeckButton bg='brand.500' color='white' mx="0.5em" onClick={() => handleCloseModal()} label='OK' />
				</Flex>

			</Box>
		)
	}

}


function mapStateToProps(state) {
	return {
		manageAdminOrgUsersOutcome: state.manageAdminOrgUsersOutcome.results.data,
		isMobileView: state.mobileViewMode.isMobileView
	};
}

const mapDispatchToProps = (dispatch) =>
	bindActionCreators(
		{
			createNewOrgUser,
			createOrgUserProfileReset
		},
		dispatch,
	)

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AddNewOrgUserForm));
